import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const LOCATION = "Houston, TX, USA";
const SUPPORT_EMAIL = "support@aipromptdojo.com";
const LEGAL_EMAIL = "support@aipromptdojo.com";
const SITE = "aipromptdojo.com";
const EFFECTIVE = "February 15, 2026";

function LegalShell({ title, children, testid }) {
  return (
    <div className="App">
      <Header />
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-14 md:py-20" data-testid={testid}>
        <Link to="/" className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)]">← Back to home</Link>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-4">{title}</h1>
        <div className="text-xs text-[var(--ink-3)] mt-2">Effective: {EFFECTIVE}</div>
        <div className="mt-8 space-y-6 text-[var(--ink-2)] leading-relaxed">{children}</div>

        <div className="mt-14 pt-8 border-t border-[var(--line)] text-sm text-[var(--ink-3)]">
          Questions? Reach us at{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">
            {SUPPORT_EMAIL}
          </a>
          .
        </div>
      </section>
      <Footer />
    </div>
  );
}

function H2({ children }) {
  return <h2 className="font-display text-2xl md:text-3xl tracking-tight text-[var(--ink)] mt-10">{children}</h2>;
}

export function Terms() {
  return (
    <LegalShell title="Terms of Service" testid="legal-terms-page">
      <p>
        Welcome to PromptDojo (the "Service", "we", "us", "our"), based in {LOCATION}.
        These Terms of Service ("Terms") govern your access to and use of {SITE} and the Service. By creating an account
        or paying for a subscription, you agree to be bound by these Terms.
      </p>

      <H2>1. Eligibility & Account</H2>
      <p>
        You must be at least 13 years of age (or the minimum age of digital consent in your jurisdiction) to use the
        Service. You agree to provide accurate information when creating an account and to keep your credentials secure.
        You are responsible for all activity on your account.
      </p>

      <H2>2. The Service</H2>
      <p>
        PromptDojo provides interactive lessons, AI-graded exercises, templates, and related learning features for AI
        prompting skills. We may update, modify, or discontinue features at any time. We strive to maintain availability
        but do not guarantee uninterrupted service.
      </p>

      <H2>3. Subscriptions, Pricing & Billing</H2>
      <p>
        The paid plan ("AI Mastery") is available as a monthly subscription at $9.99/month or an annual subscription at
        $99.00/year. Each grants access to unlimited daily challenges, the full challenge archive, advanced learning paths,
        and 150 AI Power credits per billing period. Payments are processed by Stripe. By subscribing, you authorize us to
        charge the payment method on file at the selected interval. Prices may change with at least 14 days' notice posted
        on the Service. Subscribers from earlier "Career" or "Pro" plans retain access on their original terms until their
        current paid term ends.
      </p>
      <p>
        Promotional pricing (including referral-discounted first periods) applies only to the first billing period;
        subsequent renewals are at standard published rates. Optional AI Power credit packs may be purchased separately
        and are non-refundable once delivered to your account.
      </p>

      <H2>4. Refunds</H2>
      <p>
        We offer a 7-day money-back guarantee on first-time paid subscriptions — see our{" "}
        <Link to="/refund" className="text-[var(--accent)] hover:underline">Refund Policy</Link> for details.
      </p>

      <H2>5. License & Acceptable Use</H2>
      <p>
        We grant you a limited, non-exclusive, non-transferable, revocable license to access the Service for your
        personal or internal business use. You agree not to:
      </p>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>copy, scrape, or redistribute Service content (lessons, templates, AI feedback)</li>
        <li>reverse engineer or attempt to extract our prompts, models, or grading rubrics</li>
        <li>use the Service to generate content that is unlawful, abusive, or infringes others' rights</li>
        <li>resell, rebrand, or relicense the Service without prior written consent</li>
        <li>share a single account with multiple users (use our team plan instead)</li>
      </ul>

      <H2>6. User Content</H2>
      <p>
        Prompts and responses you submit ("User Content") remain yours. By submitting them, you grant us a worldwide,
        royalty-free license to process and display them as needed to operate the Service, including sending them to
        AI grading providers. We may use aggregated, anonymized data to improve the Service.
      </p>

      <H2>7. Intellectual Property</H2>
      <p>
        All lessons, templates, branding, code, and other materials provided by PromptDojo are owned by us or our
        licensors and protected by intellectual property laws. Nothing in these Terms grants you ownership of our IP.
      </p>

      <H2>8. Third-Party Services</H2>
      <p>
        The Service relies on third-party providers including Google (authentication), Stripe (payments), and Anthropic
        (AI grading via Claude). Your use of those features is also subject to their respective terms.
      </p>

      <H2>9. Termination</H2>
      <p>
        You may cancel your subscription at any time; access continues until the end of the current paid period. We may
        suspend or terminate your access for violation of these Terms, fraud, chargebacks, or as required by law.
      </p>

      <H2>10. Disclaimers</H2>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
        MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. AI-GENERATED FEEDBACK IS FOR
        EDUCATIONAL PURPOSES AND MAY CONTAIN INACCURACIES. WE DO NOT GUARANTEE ANY SPECIFIC OUTCOME, INCOME, OR CAREER
        RESULT FROM USE OF THE SERVICE.
      </p>

      <H2>11. Limitation of Liability</H2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIMS ARISING FROM YOUR USE OF THE SERVICE
        IS LIMITED TO THE AMOUNT YOU PAID US IN THE 12 MONTHS PRIOR TO THE EVENT GIVING RISE TO THE CLAIM. WE ARE NOT
        LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES.
      </p>

      <H2>12. Governing Law</H2>
      <p>
        These Terms are governed by the laws of the State of Texas, USA, without regard to conflict-of-law rules. Any
        dispute will be resolved exclusively in the state or federal courts located in Harris County, Texas, and you
        consent to that jurisdiction.
      </p>

      <H2>13. Changes to These Terms</H2>
      <p>
        We may update these Terms from time to time. Material changes will be posted on this page with an updated
        "Effective" date. Continued use of the Service after changes constitutes acceptance.
      </p>

      <H2>14. Contact</H2>
      <p>
        Questions about these Terms? Email{" "}
        <a href={`mailto:${LEGAL_EMAIL}`} className="text-[var(--accent)] hover:underline">{LEGAL_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell title="Privacy Policy" testid="legal-privacy-page">
      <p>
        This Privacy Policy explains how PromptDojo (based in {LOCATION}) collects, uses, and
        protects your information when you use {SITE}.
      </p>

      <H2>1. Information We Collect</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>
          <strong>Account info</strong> — your name, email address, and profile photo from Google when you sign in
          via Google OAuth.
        </li>
        <li>
          <strong>Usage data</strong> — lessons started/completed, progress data, streak counts, scores, and timestamps so we can
          show your progress and weekly leaderboard.
        </li>
        <li>
          <strong>Prompts & responses</strong> — the prompts you submit to lessons, which we send to our AI grading
          provider (Anthropic Claude) and store with your account history.
        </li>
        <li>
          <strong>Payment metadata</strong> — Stripe handles all card data; we only store transaction IDs, amount,
          plan, and timestamps. We never see your card number.
        </li>
        <li>
          <strong>Referral data</strong> — your referral code, who you referred, and reward credits.
        </li>
      </ul>

      <H2>2. How We Use Information</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>To operate and improve the Service (grading, leaderboard, progress tracking, customer support)</li>
        <li>To process payments and manage subscriptions</li>
        <li>To send transactional emails (receipts, billing notices, account changes)</li>
        <li>To comply with legal obligations and detect fraud</li>
      </ul>
      <p>We do not sell your personal data. We do not use your prompts to train public AI models.</p>

      <H2>3. Cookies & Local Storage</H2>
      <p>
        We use a session cookie ("session_token") to keep you logged in. We use browser localStorage to temporarily
        capture referral codes from share links. We do not use cross-site advertising cookies.
      </p>

      <H2>4. Third Parties We Share With</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li><strong>Google</strong> — for authentication only (we receive name, email, photo)</li>
        <li><strong>Stripe</strong> — for payment processing (subject to Stripe's Privacy Policy)</li>
        <li><strong>Anthropic (Claude)</strong> — receives prompts you submit, used for grading only</li>
        <li><strong>MongoDB Atlas</strong> — our database hosting provider</li>
      </ul>

      <H2>5. Data Retention</H2>
      <p>
        We keep your account data for as long as your account is active. You may request deletion by emailing{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>.
        We honor verified deletion requests within 30 days, except where retention is required by law (e.g., tax
        records).
      </p>

      <H2>6. Your Rights</H2>
      <p>
        Depending on your jurisdiction (GDPR, CCPA, etc.), you may have the right to access, correct, delete, or export
        your personal data. Contact{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>{" "}
        and we'll respond within 30 days.
      </p>

      <H2>7. Security</H2>
      <p>
        We use HTTPS in transit, secure cloud hosting, and limit access to user data on a need-to-know basis. No system
        is 100% secure, but we work hard to protect your data.
      </p>

      <H2>8. Children</H2>
      <p>The Service is not directed to children under 13. We do not knowingly collect data from children under 13.</p>

      <H2>9. International Transfers</H2>
      <p>
        Our servers are located in the United States. By using the Service from outside the U.S., you consent to your
        data being processed in the U.S.
      </p>

      <H2>10. Changes</H2>
      <p>
        We may update this Privacy Policy from time to time. Material changes will be posted on this page with an
        updated "Effective" date.
      </p>

      <H2>11. Contact</H2>
      <p>
        Privacy questions? Email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>.
      </p>
    </LegalShell>
  );
}

export function Refund() {
  return (
    <LegalShell title="Refund Policy" testid="legal-refund-page">
      <p>
        We want you to be confident in your purchase. That's why PromptDojo offers a clear, no-tricks refund policy on
        all paid subscriptions.
      </p>

      <H2>7-Day Money-Back Guarantee</H2>
      <p>
        If you're not satisfied with your first paid AI Mastery subscription, you can request a full refund within
        <strong> 7 days of your initial payment</strong>, no questions asked. Just email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>{" "}
        from the email address on your account.
      </p>

      <H2>How to Request a Refund</H2>
      <ol className="list-decimal pl-6 space-y-1.5">
        <li>Email <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a> within 7 days of your payment.</li>
        <li>Use the subject line: <strong>"Refund request"</strong>.</li>
        <li>Include the email address on your account so we can locate the transaction.</li>
        <li>We'll process the refund within 3 business days. Bank/card processing may take 5–10 additional business days for the funds to appear on your statement.</li>
      </ol>

      <H2>What's Eligible</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>First-time AI Mastery subscription — monthly or annual (including referral-discounted first periods)</li>
        <li>Within 7 days of the initial charge</li>
      </ul>

      <H2>What's Not Eligible</H2>
      <ul className="list-disc pl-6 space-y-1.5">
        <li>Renewal payments after the first 30-day period (you can cancel future renewals at any time before they bill — your access continues until the period ends)</li>
        <li>Accounts terminated for violations of our <Link to="/terms" className="text-[var(--accent)] hover:underline">Terms of Service</Link></li>
        <li>Team / business / volume plans (handled per the agreement signed for those plans)</li>
      </ul>

      <H2>Cancellations</H2>
      <p>
        You can stop renewals at any time from your <Link to="/settings" className="text-[var(--accent)] hover:underline">Settings</Link>{" "}
        page (or by emailing us). Cancellation stops future charges but does not refund the current period — your access
        continues until the end of the paid 30-day period.
      </p>

      <H2>Chargebacks</H2>
      <p>
        If you have a billing issue, please email us first — we'll work to resolve it quickly. Filing a chargeback
        without contacting us first may result in account suspension.
      </p>

      <H2>Contact</H2>
      <p>
        Refund questions? Email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>{" "}
        — we usually reply within 1 business day.
      </p>
    </LegalShell>
  );
}

export function Contact() {
  return (
    <LegalShell title="Contact Us" testid="legal-contact-page">
      <p>
        We're a small team, and every email is read by a human. Reach out anytime — for support, billing questions,
        partnerships, or to say hi.
      </p>

      <H2>Email</H2>
      <p>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="text-[var(--accent)] hover:underline text-lg"
          data-testid="contact-email-link"
        >
          {SUPPORT_EMAIL}
        </a>
        <br />
        <span className="text-sm text-[var(--ink-3)]">Typical response time: within 1 business day.</span>
      </p>

      <H2>Location</H2>
      <p>
        PromptDojo is based in <strong>{LOCATION}</strong>.
      </p>

      <H2>For Teams & Businesses</H2>
      <p>
        Looking for team pricing or a custom rollout for your company? Visit our{" "}
        <Link to="/business" className="text-[var(--accent)] hover:underline">For Teams</Link> page and submit an
        inquiry — we'll get back to you with a tailored quote.
      </p>

      <H2>Press & Partnerships</H2>
      <p>
        Email{" "}
        <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--accent)] hover:underline">{SUPPORT_EMAIL}</a>{" "}
        with the subject line "Press" or "Partnership" and we'll route accordingly.
      </p>

      <H2>Legal Notices</H2>
      <p>
        For privacy requests, refund issues, or legal matters, email{" "}
        <a href={`mailto:${LEGAL_EMAIL}`} className="text-[var(--accent)] hover:underline">{LEGAL_EMAIL}</a>{" "}
        with a clear subject line.
      </p>
    </LegalShell>
  );
}
